<script setup>
import { ref, nextTick } from "vue";
import BaseInput from "./BaseInput.vue";
import BaseButton from "./BaseButton.vue";
import IconCheck from "../icons/IconCheck.vue";
import { useLeadForm } from "../../composables/useLeadForm";

const props = defineProps({
  // "hero" asks for name + phone only; "full" also asks for the business name.
  variant: { type: String, default: "full" },
  formId: { type: String, required: true },
  idPrefix: { type: String, default: "lead" },
  submitLabel: { type: String, default: "שלחו פרטים" },
  businessPlaceholder: { type: String, default: "הסטודיו של ישראל" },
  tone: { type: String, default: "light" }, // light | ink
});

const compact = props.variant === "hero";

const {
  fields,
  errors,
  state,
  isLoading,
  FormState,
  onFirstInput,
  validateField,
  submit,
  reset,
} = useLeadForm({
  formId: props.formId,
  requireBusiness: !compact,
});

const root = ref(null);

async function onSubmit() {
  const result = await submit();
  if (!result.ok && result.focus) {
    await nextTick();
    const map = {
      name: `${props.idPrefix}-name`,
      businessName: `${props.idPrefix}-business`,
      phone: `${props.idPrefix}-phone`,
    };
    document.getElementById(map[result.focus])?.focus();
  }
}
</script>

<template>
  <div ref="root" class="lead-form" :class="`lead-form--${tone}`">
    <!-- Success -->
    <div
      v-if="state === FormState.Success"
      class="lead-form__result pop-in"
      role="status"
    >
      <span class="lead-form__tick"><IconCheck /></span>
      <h3 class="lead-form__result-title">קיבלנו את הפרטים</h3>
      <p class="lead-form__result-text">ניצור איתכם קשר בהקדם.</p>
    </div>

    <!-- Rate limited -->
    <div
      v-else-if="state === FormState.Blocked"
      class="lead-form__result"
      role="status"
    >
      <h3 class="lead-form__result-title">שלחתם כבר כמה בקשות</h3>
      <p class="lead-form__result-text">
        קיבלנו אותן. אם זה דחוף, כתבו לנו ל־info@applates.info.
      </p>
    </div>

    <!-- The form -->
    <form v-else class="lead-form__form" novalidate @submit.prevent="onSubmit">
      <div class="lead-form__fields" :class="{ 'is-compact': compact }">
        <BaseInput
          :id="`${idPrefix}-name`"
          v-model="fields.name"
          label="שם מלא"
          autocomplete="name"
          placeholder="ישראל ישראלי"
          required
          :error="errors.name"
          @focus="onFirstInput"
          @blur="validateField('name')"
        />

        <BaseInput
          v-if="!compact"
          :id="`${idPrefix}-business`"
          v-model="fields.businessName"
          label="שם העסק"
          autocomplete="organization"
          :placeholder="businessPlaceholder"
          required
          :error="errors.businessName"
          @focus="onFirstInput"
          @blur="validateField('businessName')"
        />

        <BaseInput
          :id="`${idPrefix}-phone`"
          v-model="fields.phone"
          label="טלפון נייד"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
          placeholder="050-123-4567"
          required
          :error="errors.phone"
          @focus="onFirstInput"
          @blur="validateField('phone')"
        />
      </div>

      <BaseButton
        type="submit"
        size="lg"
        block
        :loading="isLoading"
        class="lead-form__submit"
      >
        {{ isLoading ? "שולח..." : submitLabel }}
      </BaseButton>

      <p
        v-if="state === FormState.Error"
        class="lead-form__error"
        role="alert"
      >
        משהו השתבש בשליחה.
        <button type="button" class="lead-form__retry" @click="reset">
          נסו שוב
        </button>
      </p>

      <p class="lead-form__note">
        נחזור אליכם בשיחה קצרה. בלי פרטי אשראי.
      </p>

      <p class="lead-form__note lead-form__note--demo">
        ההדגמה מתקיימת בזום ונמשכת 30 דקות.
      </p>
    </form>
  </div>
</template>

<style scoped>
.lead-form__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.lead-form__fields {
  display: grid;
  gap: var(--space-4);
}

@media (min-width: 560px) {
  .lead-form__fields.is-compact {
    grid-template-columns: 1fr 1fr;
  }
}

.lead-form__submit {
  margin-block-start: var(--space-1);
}

.lead-form__note {
  font-size: var(--text-sm);
  color: var(--ink-subtle);
  text-align: center;
  margin: 0;
}

/* Sits directly under the note above, as one block. */
.lead-form__note--demo {
  margin-block-start: calc(var(--space-4) * -1 + var(--space-1));
}

.lead-form__error {
  font-size: var(--text-sm);
  color: var(--danger);
  text-align: center;
  margin: 0;
}

.lead-form__retry {
  color: var(--brand);
  font-weight: var(--weight-semibold);
  text-decoration: underline;
}

/* --------------------------------------------------------------- results */

.lead-form__result {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  text-align: center;
  min-block-size: 220px;
  padding: var(--space-6) var(--space-4);
}

.lead-form__tick {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 56px;
  block-size: 56px;
  border-radius: 50%;
  background: var(--success-soft);
  color: var(--success);
  margin-block-end: var(--space-2);
}

.lead-form__tick :deep(svg) {
  inline-size: 28px;
  block-size: 28px;
  stroke-width: 2.4;
}

.lead-form__result-title {
  font-size: var(--text-xl);
  color: var(--ink);
}

.lead-form__result-text {
  color: var(--ink-muted);
  margin: 0;
}

/* ---------------------------------------------------- on a dark backdrop */

.lead-form--ink .lead-form__note {
  color: var(--on-ink-muted);
}

.lead-form--ink .lead-form__result-title {
  color: var(--on-ink);
}

.lead-form--ink .lead-form__result-text {
  color: var(--on-ink-muted);
}

.lead-form--ink :deep(.field__label) {
  color: var(--on-ink);
}

.lead-form--ink :deep(.field__optional) {
  color: var(--on-ink-muted);
}
</style>
